import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import Seo from '@/components/Seo';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const nav = useNavigate();
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (pwd !== confirm) { setError('Passwords do not match.'); return; }
    setBusy(true); setError('');
    try {
      await api.post('/auth/reset-password', { token, new_password: pwd });
      setDone(true);
      toast.success('Password reset — you can now log in');
      setTimeout(() => nav('/login'), 1800);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Could not reset your password.');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-6 sm:p-12">
      <Seo title="Reset password" noindex />
      <div className="tm-card p-8 w-full max-w-md space-y-5" data-testid="reset-password-page">
        {!token ? (
          <div className="text-center space-y-3" data-testid="reset-no-token">
            <h1 className="font-display font-semibold text-2xl uppercase tracking-tight">Invalid link</h1>
            <p className="text-sm text-slate-600">This reset link is missing or broken. Please request a new one.</p>
            <Link to="/forgot-password" className="tm-btn w-full justify-center inline-flex">Request a new link</Link>
          </div>
        ) : done ? (
          <div className="text-center space-y-3" data-testid="reset-done">
            <CheckCircle2 className="mx-auto text-emerald-600" size={40} />
            <h1 className="font-display font-semibold text-2xl uppercase tracking-tight">Password reset</h1>
            <p className="text-sm text-slate-600">Taking you to the log in page…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <h1 className="font-display font-semibold text-3xl uppercase tracking-tight">Set a new password</h1>
            <p className="text-sm text-slate-600 -mt-3">Choose a new password for your account.</p>
            <div>
              <label className="tm-label">New password</label>
              <input data-testid="reset-new" type="password" autoComplete="new-password" required value={pwd} onChange={(e) => setPwd(e.target.value)} className="tm-input" placeholder="At least 8 characters" />
            </div>
            <div>
              <label className="tm-label">Confirm new password</label>
              <input data-testid="reset-confirm" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="tm-input" placeholder="Re-type new password" />
            </div>
            {error && <p className="text-sm font-mono text-red-600 bg-red-50 border border-red-600 p-2" data-testid="reset-error">{error}</p>}
            <button data-testid="reset-submit" disabled={busy || !pwd || !confirm} className="tm-btn w-full">{busy ? 'Resetting…' : 'Reset password'}</button>
          </form>
        )}
      </div>
    </div>
  );
}
